import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { EMPTY, fromEvent, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { SummaryService } from '../../services/summary.service';
import { ApiService } from 'src/app/_shared/services/api.service';
import {
  Offers,
  RedeemTile
} from 'src/app/_shared/models/redeem-points-models/redeem-points';
import { environment } from 'src/environments/environment';
import { AemService } from '../../../_shared/services/aem-content.service';
import { AccountResponse } from 'src/app/_shared/models/account-models/account-response';
import { Card } from 'src/app/_shared/models/card-models/card';
import { EarningsResponse } from 'src/app/_shared/models/earning-models/earnings-response';
import { UserService } from '../../services/user.service';
import { AdobeDtmService } from '../../../_shared/services/adobe-dtm.service';

@Component({
  selector: 'app-redeem-points',
  templateUrl: './redeem-points.component.html',
  styleUrl: './redeem-points.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RedeemPointsComponent implements OnInit, AfterViewInit, OnDestroy {
  currentOffer: Offers;
  offers: Offers[] = [];
  currentIndex = 0;
  pages: any[] = [];
  currentPage = 0;
  aemContent: any = '';
  showOfferModal: boolean = false;
  showRedeemModal: boolean = false;
  cardWidth = 396;
  totalPages = 0;
  currentTile: RedeemTile;
  tiles: RedeemTile[] = [];
  componentDestroyed = new Subject<void>();
  hasCardOffers: boolean;
  user: AccountResponse;
  cardMemberOffers: Offers[] = [];
  userSubscription: Subscription;
  currentRewardCard: Card;
  userEarnings: EarningsResponse;
  digitalData;

  constructor(
    private readonly apiService: ApiService,
    private aem: AemService,
    private readonly summaryService: SummaryService,
    private readonly userService: UserService,
    private readonly api: ApiService,
    private adobeDTMService: AdobeDtmService
  ) {}

  @ViewChild('carousel', { static: true })
  carousel!: ElementRef<HTMLDivElement>;

  @ViewChildren('cardEl') cardElements!: QueryList<ElementRef<HTMLDivElement>>;

  @HostListener('window:resize')
  onResize() {
    this.calculateTotalPages();
    this.setTileWidth();
  }

  ngOnInit(): void {
    this.getContent();
    this.setTileWidth();
    this.getUserInfo();
    this.setAnalytics();
  }

  ngAfterViewInit() {
    fromEvent(this.carousel.nativeElement, 'scroll')
      .pipe(debounceTime(100))
      .subscribe(() => this.handleScroll());
  }
  setAnalytics() {
    // Set up digital data object
    this.digitalData = {
      pageInfo: {
        cardType: '',
        cardTypeOwned: '',
        loginType: '',
        offerID: '',
        pageName: 'redeem | logged | gmb',
        pageSubType: '',
        pageType: 'redeem'
      }
    };
    this.digitalData = this.adobeDTMService.setupWindowPageInfoData(
      this.digitalData,
      'redeem',
      'redeem'
    );

    this.adobeDTMService.setWindowObjectData(this.digitalData);
    this.adobeDTMService.sendAdobeLaunchData();
  }

  // getContent(): void {
  //   this.apiService.getAemPage('Redeem-Points').subscribe(data =>{
  //     this.aemContent = data;
  //     this.tiles = JSON.parse(data.buyTiles).Tiles;
  //     this.offers = JSON.parse(data.offers).offers;
  //     this.tiles.forEach(list => {
  //       list.image = `${environment.API_URL}/getAemImage?image=${list.image}`;
  //       list.mobileImage = `${environment.API_URL}/getAemImage?image=${list.mobileImage}`;
  //     });
  //     this.getUserInfo();
  //     this.calculateTotalPages();
  //   })
  // }

  getUserInfo() {
    this.userSubscription = this.userService.user$.subscribe(
      (user: AccountResponse) => {
        this.user = user;
        if (this.summaryService.getCardMemberRedemptionInfo()) {
          this.userEarnings = this.summaryService.getCardMemberRedemptionInfo();
          this.initializeCardMemberOffers();
        } else {
          if (!user) {
            return;
          }
          this.api
            .getEarningsStatement(user?.accountDetails.trackID)
            .subscribe(data => {
              this.summaryService.setCardMemberRedemptionInfo(data);
              this.userEarnings = data;
              this.initializeCardMemberOffers();
            });
        }
      }
    );
  }

  getContent() {
    this.aem
      .getRedeemContent()
      .pipe(
        takeUntil(this.componentDestroyed),
        switchMap((content: any) => {
          if (Object.keys(content).length === 0) {
            return this.aem.fetchContent('redeem-points');
          }
          return of(content);
        }),
        tap((content: any) => {
          if (!content) {
            return;
          }
          this.aemContent = content;

          this.tiles = JSON.parse(content.buyTiles).Tiles;
          this.offers = JSON.parse(content.offers).offers;
          this.tiles.forEach(list => {
            list.image = `${environment.API_URL}/getAemImage?image=${list.image}`;
            list.mobileImage = `${environment.API_URL}/getAemImage?image=${list.mobileImage}`;
          });

          this.calculateTotalPages();
        }),
        catchError(error => {
          this.aemContent = '';
          this.tiles = [];
          this.offers = [];
          return EMPTY; // Return an empty Observable
        })
      )
      .subscribe();
  }

  initializeCardMemberOffers(): void {
    const currentDate: Date = new Date();
    const data = this.offers;
    // Check for offers with showWithoutUserOfferId
    for (const offerId in data) {
      if (!Object.prototype.hasOwnProperty.call(data, offerId)) {
        continue;
      }
      const offer: Offers = data[offerId];

      if (!offer.showWithoutUserOfferId) {
        continue;
      }
      const startDate = new Date(offer.startDate);
      const expirationDate = new Date(offer.expirationDate);
      if (currentDate >= startDate && currentDate <= expirationDate) {
        this.cardMemberOffers.push(offer);
      }
    }

    // Display offers associated with user
    this.userEarnings?.offers?.forEach(offer => {
      const currentOffer = data[offer.offerID];
      if (typeof data[offer.offerID] !== 'undefined') {
        const startDate = new Date(currentOffer.startDate);
        const expirationDate = new Date(currentOffer.expirationDate);
        if (currentDate >= startDate && currentDate <= expirationDate) {
          this.cardMemberOffers.push(currentOffer);
        }
      }
    });

    this.cardMemberOffers?.length
      ? (this.hasCardOffers = true)
      : (this.hasCardOffers = false);

    this.setCurrentOffer();
  }

  setTileWidth(): void {
    this.cardWidth = window.innerWidth < 776 ? 316 : 396;
  }

  goToDealerLocator(): void {
    window.open(this.aemContent.button1Url, '_blank');
  }

  // Offer Carousel
  setCurrentOffer(): void {
    this.currentOffer = this.cardMemberOffers[this.currentIndex];
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.cardMemberOffers.length;
    this.setCurrentOffer();
  }

  prevSlide() {
    this.currentIndex =
      (this.currentIndex - 1 + this.cardMemberOffers.length) %
      this.cardMemberOffers.length;
    this.setCurrentOffer();
  }

  goToSlide(index: number) {
    this.currentIndex = index;
    this.setCurrentOffer();
  }

  // Redeem Buy Carousel
  get paginationArray(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  private getFullyVisibleCardIndices(): number[] {
    const container = this.carousel.nativeElement;
    const containerLeft = container.scrollLeft;
    const containerRight = containerLeft + container.offsetWidth;

    const indices: number[] = [];

    this.cardElements.forEach((cardEl, index) => {
      const card = cardEl.nativeElement;
      const cardLeft = card.offsetLeft;
      const cardRight = cardLeft + card.offsetWidth;

      if (cardLeft >= containerLeft && cardRight <= containerRight) {
        indices.push(index);
      }
    });

    return indices;
  }

  calculateTotalPages() {
    const container = this.carousel.nativeElement;
    const visibleCount = Math.floor(container.offsetWidth / this.cardWidth);
    this.totalPages = Math.ceil(this.tiles.length / visibleCount);
  }

  scrollLeft() {
    const firstFullyVisible = this.getFullyVisibleCardIndices()[0];
    const targetIndex = Math.max(firstFullyVisible - this.cardsPerPage(), 0);
    this.scrollToCard(targetIndex);
  }

  scrollRight() {
    const fullyVisible = this.getFullyVisibleCardIndices();
    const lastFullyVisible = fullyVisible[fullyVisible.length - 1] ?? 0;
    const targetIndex = Math.min(lastFullyVisible + 1, this.tiles.length - 1);
    this.scrollToCard(targetIndex);
  }

  goToPage(pageIndex: number) {
    const index = pageIndex * this.cardsPerPage();
    this.scrollToCard(index);
  }

  handleScroll() {
    const container = this.carousel.nativeElement;
    const scrollLeft = container.scrollLeft;
    const visibleWidth = container.offsetWidth;
    const totalScrollableWidth = container.scrollWidth;

    const cardsPerPage = this.cardsPerPage();
    const pageWidth = cardsPerPage * this.cardWidth;
    const maxPageIndex = this.totalPages - 1;

    const pageIndex = Math.round(scrollLeft / pageWidth);
    const nearEnd = scrollLeft + visibleWidth >= totalScrollableWidth - 1;

    if (nearEnd) {
      this.currentPage = maxPageIndex;
    } else {
      this.currentPage = pageIndex;
    }
  }

  private scrollToCard(index: number) {
    const targetCard = this.cardElements.get(index)?.nativeElement;
    if (targetCard) {
      const container = this.carousel.nativeElement;
      container.scrollTo({
        left: targetCard.offsetLeft,
        behavior: 'smooth'
      });
    }
  }

  private cardsPerPage(): number {
    const container = this.carousel.nativeElement;
    return Math.floor(container.offsetWidth / this.cardWidth);
  }

  openOfferModal(): void {
    this.showOfferModal = true;
  }

  openRedeemModal(selectedTile: RedeemTile): void {
    this.currentTile = selectedTile;
    this.showRedeemModal = true;
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }
}
